import { useEffect, useRef, useState } from 'react';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import {
  readWorkingTutorReturn,
  keepWorkingTutorReturn,
  type WorkingTutorTurn,
} from './workingTutorReturnCache';
import type { TutorUpload } from './tutorImages';
import type { TutorChatRequest } from './tutorChat';
interface Pending {
  id: string;
  fingerprint: string;
  text: string;
  request: TutorChatRequest;
  uploads: TutorUpload[];
}
export function useWorkingTutorConversation(
  initialMessage = 'Help me check my working.',
  scope = '',
) {
  const owner = useAuthStore(() => getKidSub());
  const identity = `${owner}:${scope}`;
  const current = useRef(identity);
  const generation = useRef(0);
  if (current.current !== identity) {
    current.current = identity;
    generation.current += 1;
  }
  const [storedIdentity, setStoredIdentity] = useState(identity);
  const [uploads, setUploads] = useState<TutorUpload[]>([]);
  const [message, setMessage] = useState(() =>
    readWorkingTutorReturn(owner, scope).length ? '' : initialMessage,
  );
  const [turns, setTurns] = useState<WorkingTutorTurn[]>(() =>
    readWorkingTutorReturn(owner, scope),
  );
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [terminal, setTerminal] = useState(false);
  const active = useRef(false);
  useEffect(() => {
    if (storedIdentity === identity) return;
    const saved = readWorkingTutorReturn(owner, scope);
    setTurns(saved);
    setStoredIdentity(identity);
    setMessage(saved.length ? '' : initialMessage);
    setUploads([]);
    setPending(null);
    setBusy(false);
    setError('');
    setTerminal(false);
    active.current = false;
  }, [identity, storedIdentity, owner, scope, initialMessage]);
  useEffect(() => {
    if (storedIdentity === identity) keepWorkingTutorReturn(owner, scope, turns);
  }, [turns, identity, storedIdentity, owner, scope]);
  return {
    uploads,
    setUploads,
    message,
    setMessage,
    turns: storedIdentity === identity ? turns : [],
    setTurns,
    pending,
    setPending,
    busy,
    setBusy,
    error,
    setError,
    terminal,
    setTerminal,
    active,
    generation,
  };
}
